import { SECTION_TYPES } from "./constants";

/**
 * Parse raw content into structured sections
 * Uses heuristic patterns to detect section types
 */
export const parseContent = (rawText) => {
  if (!rawText || !rawText.trim()) return [];

  const lines = rawText.split("\n");
  const sections = [];
  let currentBuffer = [];
  let currentType = null;

  const flushBuffer = () => {
    if (currentBuffer.length === 0) return;
    const content = currentBuffer.join("\n").trim();
    if (content) {
      sections.push([currentType || SECTION_TYPES.P, content]);
    }
    currentBuffer = [];
    currentType = null;
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    // Detect heading patterns
    const h1Match = trimmed.match(/^#{1}\s+(.+)$/);
    const h2Match = trimmed.match(/^#{2}\s+(.+)$/);
    const h3Match = trimmed.match(/^#{3}\s+(.+)$/);

    if (h1Match) {
      flushBuffer();
      sections.push([SECTION_TYPES.H1, h1Match[1].trim()]);
    } else if (h2Match) {
      flushBuffer();
      sections.push([SECTION_TYPES.H2, h2Match[1].trim()]);
    } else if (h3Match) {
      flushBuffer();
      sections.push([SECTION_TYPES.H3, h3Match[1].trim()]);
    }
    // Empty line = section break
    else if (trimmed === "") {
      flushBuffer();
    }
    // List items
    else if (trimmed.match(/^[-*]\s+.+/)) {
      if (currentType !== SECTION_TYPES.UL) {
        flushBuffer();
        currentType = SECTION_TYPES.UL;
      }
      const itemText = trimmed.replace(/^[-*]\s+/, "");
      currentBuffer.push(itemText);
    }
    // Table detection (pipes)
    else if (trimmed.includes("|")) {
      if (currentType !== SECTION_TYPES.TABLE) {
        flushBuffer();
        currentType = SECTION_TYPES.TABLE;
      }
      currentBuffer.push(line);
    }
    // YouTube detection
    else if (trimmed.match(/https?:\/\/(www\.)?youtube\.com/i)) {
      flushBuffer();
      sections.push([SECTION_TYPES.YT, trimmed]);
    }
    // YouTube short URL detection
    else if (trimmed.match(/https?:\/\/youtu\.be/i)) {
      flushBuffer();
      sections.push([SECTION_TYPES.YT, trimmed]);
    }
    // Regular paragraph
    else {
      if (currentType !== SECTION_TYPES.P && currentType !== null) {
        flushBuffer();
      }
      currentType = SECTION_TYPES.P;
      currentBuffer.push(line);
    }
  }

  flushBuffer();
  return sections;
};

/**
 * Detect if a line looks like a button definition
 * Format: Label||URL
 */
export const detectButton = (text) => {
  const match = text.match(/^(.+?)\|\|(.+?)$/);
  return match ? { label: match[1].trim(), url: match[2].trim() } : null;
};

/**
 * Detect if a line looks like a resource
 * Format: Title||URL
 */
export const detectResource = (text) => {
  const match = text.match(/^(.+?)\|\|(.+?)$/);
  return match ? { title: match[1].trim(), url: match[2].trim() } : null;
};

/**
 * Detect alert type and message
 * Format: type||message
 */
export const detectAlert = (text) => {
  const match = text.match(/^(warning|error|info|success)\|\|(.+)$/i);
  return match ? { type: match[1].toLowerCase(), message: match[2].trim() } : null;
};

/**
 * Parse list items from buffer
 */
export const parseListItems = (buffer) => {
  return buffer.join("\n").trim();
};

/**
 * Parse table rows
 * Handles pipe-delimited tables
 */
export const parseTableRows = (buffer) => {
  return buffer.map(row =>
    row.split("|").map(cell => cell.trim()).filter(cell => cell)
  );
};

/**
 * Reclassify a section (change its type)
 */
export const reclassifySection = (sections, index, newType) => {
  if (index < 0 || index >= sections.length) return sections;
  const updated = [...sections];
  updated[index] = [newType, sections[index][1]];
  return updated;
};

/**
 * Update section content
 */
export const updateSectionContent = (sections, index, newContent) => {
  if (index < 0 || index >= sections.length) return sections;
  const updated = [...sections];
  updated[index] = [sections[index][0], newContent];
  return updated;
};

/**
 * Delete a section
 */
export const deleteSection = (sections, index) => {
  if (index < 0 || index >= sections.length) return sections;
  return sections.filter((_, i) => i !== index);
};

/**
 * Move a section up
 */
export const moveSectionUp = (sections, index) => {
  if (index <= 0 || index >= sections.length) return sections;
  const updated = [...sections];
  [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
  return updated;
};

/**
 * Move a section down
 */
export const moveSectionDown = (sections, index) => {
  if (index < 0 || index >= sections.length - 1) return sections;
  const updated = [...sections];
  [updated[index + 1], updated[index]] = [updated[index], updated[index + 1]];
  return updated;
};

/**
 * Insert a new section at index
 */
export const insertSection = (sections, index, type, content = "") => {
  const updated = [...sections];
  updated.splice(index, 0, [type, content]);
  return updated;
};

/**
 * Validate section format
 */
export const isValidSection = (section) => {
  if (!Array.isArray(section) || section.length !== 2) return false;
  const [type, content] = section;
  const validTypes = Object.values(SECTION_TYPES);
  return validTypes.includes(type) && typeof content === "string";
};

/**
 * Validate all sections
 */
export const validateSections = (sections) => {
  if (!Array.isArray(sections)) return false;
  return sections.every(isValidSection);
};
